import React from 'react';
import { notification } from 'antd';
import axios from 'axios';
import { isEmpty, uniq, without } from 'lodash';
import { normalize } from 'normalizr';

import { isChatOpen, isTicketOpen, signedUploadUrl } from '../../actions';
import { isAllTasksV2FilterEnabled } from '../../js/featureFlags';
import { ACTION_TYPES } from './constants';
import SCHEMAS from './schemas';
import PROJECT_SCHEMAS from '../projects/schemas';
import {
  getOpenedTaskTitleIdentifier,
  getSortedTaskIdListBySectionId,
  getTaskById,
  getTaskFiles,
  getTaskPosition,
  getTaskProjectWithSectionIdList,
  getTaskSectionId,
  isTaskCompletedSelector,
} from './taskEntities/selectors';
import {
  getOpenedTaskId,
  getProjectTasksFilters,
  getProjectTasksSorts,
} from '../general/selectors';
import {
  getCustomFieldsForProjectId,
  getCustomFieldsIdListForProjectId,
  getProjectDataSortedSectionIdListSelector,
} from '../projects/selectors';
import { getFileActionWithRetry } from '../files/actions';
import { getProjectByIdBasicFieldsAction } from '../projects/actions';
import {
  getTaskLastReorderTimeForProjectId,
  getTaskProjectAndSectionLabelList,
} from './tasksToProjectsRelation/selectors';
import { socketSendAction } from '../state-areas/socket/actions';
import { navigateAction } from '../state-areas/navigation/actions';
import { setTeamsState } from '../../actions/toggleAction';
import { ADD_NEW_ROW_ID_PREFFIX } from '../../components/UI/molecules/virtualized-list-view/constants/virtualizedListConstants';
import { convertFiltersToQueryString } from './helpers';

const ALL_TASKS_DEFAULT_PAGE_SIZE = 20;

const baseUrl = process.env.REACT_APP_API_URL;

const config = {
  headers: { Authorization: '' },
};

export const getTasksAction =
  (projectId, filters = {}, sorts = []) =>
  async (dispatch, getState) => {
    dispatch({
      type: ACTION_TYPES.GET_TASKS_REQUEST,
    });
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      if (!projectId) return;
      // Filter out null or empty values
      const filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => {
          if (key === 'assignedTo' && Array.isArray(value)) {
            return value.length !== 0; // Skip empty assignedTo array
          }
          return value !== null && value !== '' && value !== undefined;
        }),
      );

      // Build query string based on filteredFilters
      let queryString = '';
      if (Object.keys(filteredFilters).length > 0) {
        queryString =
          '?' +
          Object.entries(filteredFilters)
            .map(
              ([key, value]) =>
                `${key}=${key === 'assignedTo' ? value.map((item) => item._id)?.join(',') : value}`,
            )
            .join('&');
      }

      if (!isEmpty(sorts)) {
        queryString +=
          (Object.keys(filteredFilters).length > 0 ? '&' : '?') +
          sorts
            .map(({ orderBy, sortBy }) => `sortBy=${sortBy}&orderBy=${orderBy}`)
            .join('&');
      }

      const res = await axios.get(
        `${baseUrl}/alltasks/${projectId}${queryString}`,
        config,
      );

      const normalizedTasksResp = normalize(
        res?.data?.data,
        SCHEMAS.GET_BOARD_TASKS_SCHEMA,
      );
      const normalizedTasksBySectionIdResp = normalize(
        res?.data?.data,
        SCHEMAS.TASKS_BY_SECTION_ID_SCHEMA,
      );

      const sections = Object.keys(
        normalizedTasksBySectionIdResp?.result?.sectionCounts || {},
      ).reduce((res, currentKey) => {
        return {
          ...res,
          [currentKey]: {
            ...(normalizedTasksBySectionIdResp?.entities?.sections?.[
              currentKey
            ] || {}),
            sectionCounts:
              normalizedTasksBySectionIdResp?.result?.sectionCounts?.[
                currentKey
              ],
            sectionDoneCounts:
              normalizedTasksBySectionIdResp?.result?.sectionDoneCounts?.[
                currentKey
              ],
          },
        };
      }, {});

      if (localStorage.getItem('virtualizedListFeatureEnabled') === 'true') {
        let pageSizeCounter = ALL_TASKS_DEFAULT_PAGE_SIZE;
        const allSectionsAndCounts =
          normalizedTasksBySectionIdResp?.result?.sectionCounts;
        const sortedSectionsIdList =
          getProjectDataSortedSectionIdListSelector(getState(), {
            projectId,
          }) || [];
        const infiniteListData = sortedSectionsIdList?.reduce(
          (result, sectionId) => {
            const virtualizedIdList = new Array(allSectionsAndCounts[sectionId])
              .fill('')
              .map((_item) => crypto.randomUUID());

            let isSectionLoaded = false;
            let prevSectionLoaded = false;
            if (pageSizeCounter > 0) {
              isSectionLoaded = true;
              prevSectionLoaded = true;
            }

            const sectionTasks = sections?.[sectionId]?.tasks || [];

            if (
              pageSizeCounter <= 0 &&
              result.prevSectionLoaded &&
              !sectionTasks.length
            ) {
              isSectionLoaded = true;
              prevSectionLoaded = true;
            }

            const sectionTasksOnPage = sectionTasks?.slice(0, pageSizeCounter);
            pageSizeCounter -= sectionTasksOnPage.length;
            const correlationsData = sectionTasksOnPage?.reduce(
              (result, taskId, currentIndex) => {
                return {
                  ...result,
                  prevSectionLoaded,
                  virtualIdsCorrelation: {
                    ...(result.virtualIdsCorrelation || {}),
                    [virtualizedIdList[currentIndex]]: taskId,
                  },
                  realIdsCorrelation: {
                    ...(result.realIdsCorrelation || {}),
                    [taskId]: virtualizedIdList[currentIndex],
                  },
                  loadedList: [
                    ...(result.loadedList || []),
                    virtualizedIdList[currentIndex],
                  ],
                };
              },
              {},
            );

            const loadedList = [
              ...(result.loadedList || []),
              ...(correlationsData.loadedList || []),
            ];

            if (isSectionLoaded) {
              loadedList.push(sectionId);
            }

            return {
              ...result,
              sections: {
                ...(result.sections || {}),
                [sectionId]: {
                  virtualizedIdList,
                },
              },
              virtualIdsCorrelation: {
                ...(result.virtualIdsCorrelation || {}),
                ...(correlationsData.virtualIdsCorrelation || {}),
              },
              realIdsCorrelation: {
                ...(result.realIdsCorrelation || {}),
                ...(correlationsData.realIdsCorrelation || {}),
              },
              loadedList,
            };
          },
          {},
        );

        dispatch({
          type: ACTION_TYPES.GET_TASKS_INFINITE_LIST_FIRST_PAGE_SUCCESS,
          payload: {
            ...infiniteListData,
            projectId,
          },
        });
      }

      const tasksResults = normalizedTasksResp?.result?.tasks;
      const taskEntities = normalizedTasksResp?.entities?.tasks;

      dispatch({
        type: ACTION_TYPES.GET_TASKS_REQUEST_SUCCESS,
        payload: {
          assignees: normalizedTasksResp?.entities?.assignees,
          users: normalizedTasksResp?.entities?.users,
          tasks: taskEntities,
          sections,
          tasksResults,
          projectId,
        },
      });

      try {
        saveRecentProject(projectId);
        localStorage.setItem(
          PROJECT_TASKS_CACHE_KEY(projectId),
          JSON.stringify({ tasks: taskEntities, sections, tasksResults }),
        );
      } catch (_) {}
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.GET_TASKS_REQUEST_ERROR,
        payload: error,
      });
    }
  };

export const getTasksInfiniteListNextItemsAction =
  (projectId, lastVisibleItemIndex) =>
  async (dispatch, getState) => {
    const fn = '[getTasksInfiniteListNextItemsAction]';
    const state = getState();
    const filtersProp = getProjectTasksFilters(state);
    const sorts = getProjectTasksSorts(state);
    const sortedSectionsIdList =
      getProjectDataSortedSectionIdListSelector(state, {
        projectId,
      }) || [];

    const loadedListWithoutSectionsLength = (
      state?.infiniteListData?.loadedList || []
    ).filter((id) => !(sortedSectionsIdList || []).includes(id))?.length;
    const loadingListWithoutSectionsLength = (
      state?.infiniteListData?.loadingList || []
    ).filter((id) => !(sortedSectionsIdList || []).includes(id))?.length;

    const skipCount =
      loadedListWithoutSectionsLength + loadingListWithoutSectionsLength;
    const takeCount =
      Math.ceil(lastVisibleItemIndex / ALL_TASKS_DEFAULT_PAGE_SIZE) *
        ALL_TASKS_DEFAULT_PAGE_SIZE +
      ALL_TASKS_DEFAULT_PAGE_SIZE -
      skipCount;

    if (takeCount <= 0) {
      return;
    }

    let skipCounter = skipCount;
    let takeCounter = takeCount;
    const allVirtualizedIds = sortedSectionsIdList?.reduce(
      (result, sectionId) => {
        const sectionVirtualizedList =
          state?.infiniteListData?.sections?.[sectionId]
            .virtualizedIdList || [];

        if (skipCounter >= sectionVirtualizedList.length) {
          skipCounter -= sectionVirtualizedList.length;
          return result;
        }

        if (!takeCounter) {
          return result;
        }

        const slicedList = sectionVirtualizedList.slice(
          skipCounter,
          takeCounter + skipCounter,
        );
        skipCounter = 0;
        takeCounter -= slicedList.length;

        const loadingList = [...(result.loadingList || []), ...slicedList];

        if (
          !isEmpty(slicedList) &&
          !(state?.infiniteListData?.loadedList || []).includes(sectionId)
        ) {
          loadingList.push(sectionId);
        }

        return {
          ...result,
          nextItemsToCorrelate: [
            ...(result.nextItemsToCorrelate || []),
            ...slicedList,
          ],
          loadingList,
        };
      },
      {},
    );

    const loadingList = allVirtualizedIds.loadingList;
    const nextItemsToCorrelate = allVirtualizedIds.nextItemsToCorrelate;

    if (isEmpty(nextItemsToCorrelate)) {
      console.debug(`${fn}: no more data to load`);

      const emptyLoadedSections = sortedSectionsIdList?.reduce(
        (result, sectionId) => {
          if (
            !(state?.infiniteListData?.loadedList || []).includes(sectionId)
          ) {
            return [...result, sectionId];
          }
          return result;
        },
        [],
      );

      if (isEmpty(emptyLoadedSections)) {
        return;
      }

      return dispatch({
        type: ACTION_TYPES.GET_TASKS_INFINITE_LIST_NEXT_PAGE_SUCCESS,
        payload: {
          sections: {},
          virtualIdsCorrelation: {},
          realIdsCorrelation: {},
          loadedList: emptyLoadedSections,
          projectId,
        },
      });
    }

    dispatch({
      type: ACTION_TYPES.GET_TASKS_INFINITE_LIST_NEXT_PAGE_REQUEST,
      payload: {
        loadingList,
        projectId,
      },
    });

    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      if (!projectId) return;

      const filters = {
        ...filtersProp,
      };

      filters.responseType = 'flat';

      // Filter out null or empty values
      const filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => {
          if (key === 'assignedTo' && Array.isArray(value)) {
            return value.length !== 0; // Skip empty assignedTo array
          }
          return value !== null && value !== '' && value !== undefined;
        }),
      );

      // Build query string based on filteredFilters
      let queryString = '';
      if (Object.keys(filteredFilters).length > 0) {
        queryString =
          '?' +
          Object.entries(filteredFilters)
            .map(
              ([key, value]) =>
                `${key}=${key === 'assignedTo' ? value.map((item) => item._id)?.join(',') : value}`,
            )
            .join('&');
      }

      if (!isEmpty(sorts)) {
        queryString +=
          (Object.keys(filteredFilters).length > 0 ? '&' : '?') +
          sorts
            .map(({ orderBy, sortBy }) => `sortBy=${sortBy}&orderBy=${orderBy}`)
            .join('&');
      }

      queryString += `${!queryString.length ? '?' : '&'}skip=${skipCount}&takeCount=${takeCount}`;

      const res = await axios.get(
        `${baseUrl}/alltasks/${projectId}${queryString}`,
        config,
      );

      const normalizedTasksResp = normalize(
        res?.data?.data,
        SCHEMAS.GET_BOARD_TASKS_SCHEMA,
      );
      const normalizedTasksBySectionIdResp = normalize(
        res?.data?.data,
        SCHEMAS.TASKS_BY_SECTION_ID_SCHEMA,
      );

      const sortedSectionsIdList =
        getProjectDataSortedSectionIdListSelector(getState(), { projectId }) ||
        [];

      let correlatedTasksCounter = 0;
      let requestedSectionReached = false;
      const fetchedSectionIds = Object.keys(
        normalizedTasksBySectionIdResp?.entities?.sections || {},
      );
      let processedSectionsCounter = fetchedSectionIds.length;

      const infiniteListData = sortedSectionsIdList.reduce(
        (result, sectionId) => {
          const sectionTasks =
            normalizedTasksBySectionIdResp?.entities?.sections?.[sectionId]
              ?.tasks || [];

          if (
            !requestedSectionReached &&
            fetchedSectionIds.includes(sectionId)
          ) {
            requestedSectionReached = true;
            processedSectionsCounter -= 1;
          }

          if (
            requestedSectionReached &&
            processedSectionsCounter > 0 &&
            !fetchedSectionIds.includes(sectionId)
          ) {
            requestedSectionReached = false;
          }

          let isSectionLoaded = false;
          if (
            sectionTasks.length > 0 ||
            (processedSectionsCounter > 0 && !requestedSectionReached)
          ) {
            isSectionLoaded = true;
          }

          const correlationsData = sectionTasks?.reduce(
            (result, taskId, currentIndex) => {
              const resultData = {
                ...result,
                virtualIdsCorrelation: {
                  ...(result.virtualIdsCorrelation || {}),
                  [nextItemsToCorrelate[correlatedTasksCounter]]: taskId,
                },
                realIdsCorrelation: {
                  ...(result.realIdsCorrelation || {}),
                  [taskId]: nextItemsToCorrelate[correlatedTasksCounter],
                },
                loadedList: [
                  ...(result.loadedList || []),
                  nextItemsToCorrelate[correlatedTasksCounter],
                ],
              };
              correlatedTasksCounter += 1;
              return resultData;
            },
            {},
          );

          const loadedList = [
            ...(result.loadedList || []),
            ...(correlationsData.loadedList || []),
          ];

          if (isSectionLoaded) {
            loadedList.push(sectionId);
          }

          return {
            ...result,
            virtualIdsCorrelation: {
              ...(result.virtualIdsCorrelation || {}),
              ...(correlationsData.virtualIdsCorrelation || {}),
            },
            realIdsCorrelation: {
              ...(result.realIdsCorrelation || {}),
              ...(correlationsData.realIdsCorrelation || {}),
            },
            loadedList,
            sections: {
              ...(result.sections || {}),
              [sectionId]: sectionTasks,
            },
          };
        },
        {},
      );

      dispatch({
        type: ACTION_TYPES.GET_TASKS_INFINITE_LIST_NEXT_PAGE_SUCCESS,
        payload: {
          ...(infiniteListData || {}),
          assignees: normalizedTasksResp?.entities?.assignees,
          users: normalizedTasksResp?.entities?.users,
          tasks: normalizedTasksResp?.entities?.tasks,
          tasksResults: normalizedTasksResp?.result?.tasks,
          projectId,
        },
      });
    } catch (error) {
      console.warn(`${fn}: Error happened`, error);
      dispatch({
        type: ACTION_TYPES.GET_TASKS_INFINITE_LIST_NEXT_PAGE_ERROR,
        payload: error,
      });
    }
  };

export const getCalendarTasksAction =
  (projectId, selectedMonth, selectedYear) => async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      if (!projectId) return;

      function getPrevNextMonth(year, month) {
        // month is 1–12 (Jan = 1, Dec = 12)

        let prevMonth, prevYear, nextMonth, nextYear;

        if (month === 1) {
          prevMonth = 12;
          prevYear = year - 1;
        } else {
          prevMonth = month - 1;
          prevYear = year;
        }

        if (month === 12) {
          nextMonth = 1;
          nextYear = year + 1;
        } else {
          nextMonth = month + 1;
          nextYear = year;
        }

        return {
          prev: { year: prevYear, month: prevMonth },
          next: { year: nextYear, month: nextMonth },
        };
      }

      const { prev, next } = getPrevNextMonth(selectedYear, selectedMonth);
      const res = await axios.get(
        `${baseUrl}/tasks/project/${projectId}?dueDateStart=${prev?.year}-${prev.month}-01&dueDateEnd=${next?.year}-${next?.month}-01`,
        config,
      );

      return res?.data?.data || [];
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.GET_TASKS_REQUEST_ERROR,
        payload: error,
      });
    }
  };

export const getSectionTasksAction =
  (sectionId, projectId, query, filters = {}) =>
  async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;

      // Filter out null or empty values
      const filteredFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => {
          if (key === 'assignedTo' && Array.isArray(value)) {
            return value.length !== 0; // Skip empty assignedTo array
          }
          return value !== null && value !== '' && value !== undefined;
        }),
      );

      // Build query string based on filteredFilters
      let queryString = '';
      if (Object.keys(filteredFilters).length > 0) {
        queryString =
          '?' +
          Object.entries(filteredFilters)
            .map(
              ([key, value]) =>
                `${key}=${key === 'assignedTo' ? value.map((item) => item._id)?.join(',') : value}`,
            )
            .join('&');
      }

      const res = await axios.get(
        `${baseUrl}/tasks/section/${sectionId}${queryString}`,
        {
          ...config,
          params: { page: query },
        });
        const normalizedResp = normalize(res?.data, SCHEMAS.GET_SECTION_TASKS_SCHEMA);

      dispatch({
        type: ACTION_TYPES.GET_SECTION_TASKS_REQUEST_SUCCESS,
        payload: {
          assignees: normalizedResp?.entities?.assignees,
          users: normalizedResp?.entities?.users,
          tasks: normalizedResp?.entities?.tasks,
          sectionId,
          projectId,
          appendedTaskIdList: normalizedResp?.result?.data,
        },
      });
      return res.data.data;
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.GET_SECTION_TASKS_REQUEST_ERROR,
        payload: error,
      });
    }
  };

export const getTaskAction = (id) => async (dispatch) => {
  try {
    dispatch({
      type: ACTION_TYPES.GET_TASK_REQUEST,
    });
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const res = await axios.get(`${baseUrl}/tasks/${id}`, config);
    const normalizedResp = normalize(res?.data, SCHEMAS.GET_TASK_SCHEMA);

    dispatch({
      type: ACTION_TYPES.GET_TASK_REQUEST_SUCCESS,
      payload: {
        taskId: normalizedResp?.result?.data,
        task: normalizedResp?.entities?.tasks?.[normalizedResp?.result?.data],
        assignees: normalizedResp?.entities?.assignees,
        users: normalizedResp?.entities?.users,
      },
    });
  } catch (error) {
    notification.error({
      message: error?.response?.data?.message || 'Oops! something Went Wrong',
      placement: 'bottomRight',
    });

    dispatch({
      type: ACTION_TYPES.GET_TASK_REQUEST_ERROR,
      payload: error?.response?.data,
    });
  }
};

export const getTaskByIdentifierAction =
  (identifier, taskIdProp, trigger, lean, callback, doNotOpen) =>
  async (dispatch) => {
    try {
      dispatch({
        type: ACTION_TYPES.GET_TASK_BY_IDENTIFIER_REQUEST,
        payload: {
          taskId: taskIdProp,
        },
      });
      dispatch({
        type: ACTION_TYPES.GET_TASK_REQUEST,
      });

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const workspaceId =
        sessionStorage.getItem('workspaceId') ||
        localStorage.getItem('workspaceId');

      // FIXME: @Umair, lean param is never used and BE returns identical response if it is specified. Is it still needed?
      const url = `${baseUrl}/tasks/${workspaceId}/identifier/${identifier}${lean ? '?lean=true' : ''}`;

      const res = await axios.get(url, config);
      const normalizedResp = normalize(res?.data, SCHEMAS.GET_TASK_SCHEMA);
      const normalizedTaskProjects = normalize(
        res?.data,
        PROJECT_SCHEMAS.GET_TASK_PROJECTS_SCHEMA,
      );
      const taskId = normalizedResp?.result?.data;

      dispatch({
        type: lean
          ? ACTION_TYPES.GET_TASK_BY_IDENTIFIER_LEAN_REQUEST_SUCCESS
          : ACTION_TYPES.GET_TASK_BY_IDENTIFIER_REQUEST_SUCCESS,
        payload: {
          taskId,
          task: normalizedResp?.entities?.tasks?.[normalizedResp?.result?.data],
          assignees: normalizedResp?.entities?.assignees,
          users: normalizedResp?.entities?.users,
          taskProjects: normalizedTaskProjects?.entities?.taskProjects,
          sectionsInfo: normalizedTaskProjects?.entities?.sections,
          subtasks: normalizedResp?.entities?.subtasks,
          doNotOpen,
        },
      });

      if (callback) {
        callback();
      }
      return normalizedResp?.result?.data;
    } catch (error) {
      const message =
        error?.response?.status === 404
          ? 'Task not found!'
          : 'You do not have access!';
      notification.error({
        message,
        placement: 'bottomRight',
      });

      const params = new URLSearchParams(window.location.search);
      params.delete('selectedTask'); // or params.set('selectedTask', 'NEW_ID');

      const newUrl = `${window.location.pathname}?${params?.toString()}`;
      window.history.replaceState({}, '', newUrl);

      if (callback) {
        callback(error?.response?.status);
      }

      dispatch({
        type: ACTION_TYPES.GET_TASK_BY_IDENTIFIER_REQUEST_ERROR,
        payload: {
          error: error?.response?.data,
          taskId: taskIdProp,
        },
      });
      dispatch(isTicketOpen(false));
    }
  };

// FIXME: no reducer for the action. Better to move to utils or add a proper action with reducer
export const getRecurringTasksAction =
  (taskId, callback) => async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const url = `${baseUrl}/tasks/${taskId}/recurring`;
      const res = await axios.get(url, config);
      callback && callback(res?.data?.data?.tasks);

      dispatch({
        type: ACTION_TYPES.GET_RECURRING_TASKS_REQUEST_SUCCESS,
        payload: res.data?.data?.tasks,
      });
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.GET_RECURRING_TASKS_REQUEST_ERROR,
        payload: error,
      });
    }
  };

const addTaskToVirtualizedListAction =
  (task, forcePlaceToLoaded) => (dispatch, getState) => {
    const fn = '[addTaskToVirtualizedListAction]';
    if (
      localStorage.getItem('virtualizedListFeatureEnabled') !== 'true' ||
      isEmpty(task)
    ) {
      return;
    }

    const state = getState();
    const infiniteListDataStore = state?.infiniteListData || {};
    const infiniteListSections = infiniteListDataStore?.sections || {};

    if (!Object.keys(infiniteListSections).includes(task?.sectionId)) {
      return;
    }

    const addedTaskId = task?._id;
    const taskSectionId = task?.sectionId;

    let virtualizedId =
      infiniteListDataStore?.realIdsCorrelation?.[addedTaskId];

    if (
      virtualizedId &&
      (
        infiniteListDataStore?.sections?.[taskSectionId]?.virtualizedIdList ||
        []
      ).includes(virtualizedId)
    ) {
      console.debug(
        `${fn}: taskId: ${addedTaskId} is already in virtualized list`,
      );
      return;
    }

    virtualizedId = crypto.randomUUID();
    const sortedSectionsIdList =
      getProjectDataSortedSectionIdListSelector(state, {
        projectId: task?.projectId,
      }) || [];

    let placeAddedItemInLoaded = forcePlaceToLoaded;
    const updatedSections = sortedSectionsIdList.reduce(
      (resultData, sectionId) => {
        if (sectionId !== taskSectionId) {
          return {
            ...resultData,
            [sectionId]: infiniteListSections?.[sectionId],
          };
        }
        const virtualizedIdList =
          task?.addedTo === 'bottom'
            ? [
                virtualizedId,
                ...(infiniteListSections?.[sectionId]?.virtualizedIdList || []),
              ]
            : [
                ...(infiniteListSections?.[sectionId]?.virtualizedIdList || []),
                virtualizedId,
              ];

        if (!forcePlaceToLoaded) {
          const isSectionLoaded =
            infiniteListDataStore?.loadedList?.includes(sectionId);
          const itemIndexInVirtualizedList = virtualizedIdList.findIndex(
            (id) => id === virtualizedId,
          );
          placeAddedItemInLoaded =
            isSectionLoaded &&
            (itemIndexInVirtualizedList === 0 ||
              infiniteListDataStore?.loadedList?.includes(
                virtualizedIdList[itemIndexInVirtualizedList - 1],
              ));
        }

        return {
          ...resultData,
          [sectionId]: {
            virtualizedIdList,
          },
        };
      },
      {},
    );

    dispatch({
      type: ACTION_TYPES.ADD_TASK_TO_INFINITE_LIST,
      payload: {
        sections: updatedSections,
        realIdsCorrelation: {
          [addedTaskId]: virtualizedId,
        },
        virtualIdsCorrelation: {
          [virtualizedId]: addedTaskId,
        },
        loadedList: placeAddedItemInLoaded ? [virtualizedId] : [],
        projectId: task?.projectId,
      },
    });
  };

export const addTaskAction = (data, callback) => async (dispatch, getState) => {
  try {
    const hasOpenedTask = getOpenedTaskId(getState()) !== null;

    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const workspaceId =
      sessionStorage.getItem('workspaceId') ||
      localStorage.getItem('workspaceId');
    const taskProperties = { ...data, workspace: workspaceId };
    const res = await axios.post(
      `${baseUrl}/tasks/add`,
      taskProperties,
      config,
    );

    const normalizedResp = normalize(res?.data, SCHEMAS.ADD_TASK_EVENT_SCHEMA);

    const task =
      normalizedResp?.entities?.tasks?.[normalizedResp?.result?.data] || {};
    const projectId = task?.projectId;
    const customFields = getCustomFieldsForProjectId(getState(), { projectId });

    dispatch(addTaskToVirtualizedListAction(task, true));

    dispatch({
      type: ACTION_TYPES.ADD_TASK_SUCCESS,
      payload: {
        taskId: normalizedResp?.result?.data,
        task: {
          ...task,
          projectAndSectionLabelList: {
            [task?.projectId]: {
              projectId: task?.projectId,
              sectionId: task?.sectionId,
            },
          },
          extendedCustomFields: res?.data?.data?.customFields
        },
        assignees: normalizedResp?.entities?.assignees,
        users: normalizedResp?.entities?.users,
        hasOpenedTask,
      },
    });

    notification.success({
      message: 'You just added a new task',
      placement: 'bottomRight',
    });

    callback && callback(res?.data?.data);
    return res?.data?.data;
  } catch (error) {
    dispatch({
      type: ACTION_TYPES.ADD_TASK_ERROR,
      payload: error,
    });
    notification.error({
      message: error?.response?.data?.message || 'Oops! somthing Went Wrong',
      placement: 'bottomRight',
    });
  }
};

const pendingTaskDeletes = new Map();

export const deleteTaskAction =
  (taskId, callback) => async (dispatch, getState) => {
    if (!taskId) return;

    const store = getState();
    const isCompleted = isTaskCompletedSelector(store, { taskId });
    const sectionId = getTaskSectionId(store, { taskId });
    const taskToDelete = getTaskById(store, { taskId });

    if (taskToDelete?.paymentTransactionId || taskToDelete?.paymentTransaction) {
      notification.error({
        message: 'Cannot delete task',
        description: 'Tasks with a payment cannot be deleted.',
        placement: 'bottomRight',
      });
      return;
    }

    const payload = { taskId, sectionId, isCompleted };
    const notifKey = `delete-task-${taskId}`;

    // Optimistically remove from UI immediately
    dispatch({ type: ACTION_TYPES.DELETE_TASK_REQUEST_SUCCESS, payload });
    dispatch({ type: 'delete-search-task', payload: taskId });
    dispatch({ type: ACTION_TYPES.DELETE_TASK_REQUEST, payload });
    dispatch({ type: ACTION_TYPES.REMOVE_TASK_FROM_INFINITE_LIST, payload: { taskId, sectionId } });

    let undone = false;

    const doDelete = async () => {
      if (undone) return;
      pendingTaskDeletes.delete(taskId);
      try {
        config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
        const res = await axios.delete(`${baseUrl}/tasks/${taskId}`, config);
        if (res?.status !== 200) throw new Error('Delete failed');
        callback && callback();
      } catch (error) {
        dispatch({ type: ACTION_TYPES.DELETE_TASK_REQUEST_ERROR, payload: error, task: taskToDelete });
        // Restore task in store
        dispatch({ type: ACTION_TYPES.ADD_TASK_SUCCESS, payload: { taskId, task: taskToDelete } });
        notification.error({ message: `Failed to delete ${taskToDelete?.titleIdentifier}`, placement: 'bottomRight' });
      }
    };

    const timer = setTimeout(doDelete, 5000);
    pendingTaskDeletes.set(taskId, timer);

    notification.success({
      key: notifKey,
      message: (
        <span>
          Deleted <strong>{taskToDelete?.titleIdentifier}</strong>
        </span>
      ),
      description: (
        <button
          onClick={() => {
            undone = true;
            clearTimeout(timer);
            pendingTaskDeletes.delete(taskId);
            notification.destroy(notifKey);
            // Restore task in store
            dispatch({ type: ACTION_TYPES.ADD_TASK_SUCCESS, payload: { taskId, task: taskToDelete } });
          }}
          style={{
            marginTop: 4,
            background: 'none',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            padding: '3px 12px',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            color: '#374151',
          }}
        >
          Undo
        </button>
      ),
      duration: 5,
      placement: 'bottomRight',
    });
  };

export const archiveTaskAction =
  (taskId, projectId, callback) => async (dispatch, getState) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(
        `${baseUrl}/tasks/${taskId}/${projectId}/archive`,
        {},
        config,
      );

      dispatch({
        type: ACTION_TYPES.DELETE_TASK_REQUEST_SUCCESS,
        payload: {
          taskId,
        },
      });
      notification.success({
        message: 'You just archived a task',
        placement: 'bottomRight',
      });
      callback && callback();
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.DELETE_TASK_REQUEST_ERROR,
        payload: error,
      });
      notification.error({
        message: 'Oops! somthing Went Wrong',
        placement: 'bottomRight',
      });
    }
  };

export const editTaskAction =
  (data, callback, errorcallback) => async (dispatch, getState) => {
    const taskId = data?._id;
    const currentTask = getTaskById(getState(), { taskId });

    try {
      const dataToUpdate = { ...data };

      await dispatch({
        type: ACTION_TYPES.EDIT_TASK,
        payload: {
          taskId,
          dataToUpdate,
        },
      });

      dispatch({
        type: 'edit-search-task',
        payload: {
          updatedTask: {
            ...data,
          },
        },
      });

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      if (data?.assignee?.length > 0) {
        dataToUpdate.assignee.map((a) => {
          return { assigneeId: a?.assigneeId?._id };
        });
      }

      await axios.put(`${baseUrl}/tasks/update`, data, config);

      dispatch({
        type: ACTION_TYPES.EDIT_TASK_SUCCESS,
        payload: {
          taskId,
        },
      });
      callback && callback();
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.EDIT_TASK_REVERT,
        payload: {
          taskId,
          task: currentTask,
          reason: error,
        },
      });
      if (errorcallback) {
        errorcallback();
      }
    }
  };

export const editTaskCustomFieldAction =
  (data, callback, errorcallback) => async (dispatch, getState) => {
    const { _id: taskId, customFieldId, customFieldValue } = data || {};

    const currentTask = getTaskById(getState(), { taskId });

    try {
      await dispatch({
        type: ACTION_TYPES.EDIT_TASK_CUSTOM_FIELD,
        payload: {
          taskId,
          customFieldId,
          customFieldValue,
        },
      });

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;

      await axios.put(`${baseUrl}/tasks/update`, data, config);

      dispatch({
        type: ACTION_TYPES.EDIT_TASK_SUCCESS,
        payload: {
          taskId,
        },
      });
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.EDIT_TASK_REVERT,
        payload: {
          taskId,
          task: currentTask,
          reason: error,
        },
      });
      if (errorcallback) {
        errorcallback();
      }
    }
  };

export const setTmpTaskProperties = (value) => ({
  type: ACTION_TYPES.SET_TMP_TASK_PROPS,
  payload: value,
});

export const copyAllTaskPropertiesToTmp = (taskId) => (dispatch, getState) => {
  const task = getTaskById(getState(), { taskId });

  if (isEmpty(task)) {
    return;
  }

  dispatch({
    type: ACTION_TYPES.SET_TMP_TASK_PROPS,
    payload: task,
  });
};

export const duplicateTaskAction =
  (taskId, data) => async (dispatch, getState) => {
    try {
      const hasOpenedTask = getOpenedTaskId(getState()) !== null;

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      // const workspaceId = sessionStorage.getItem('workspaceId') || localStorage.getItem('workspaceId');
      // const taskProperties = { ...data, workspace: workspaceId };
      const srcTask = getTaskById(getState(), { taskId });
      notification.success({
        message: srcTask?.titleIdentifier
          ? `Duplicating ${srcTask?.titleIdentifier}...`
          : 'Duplicating task...',
        placement: 'bottomRight',
      });
      const res = await axios.post(
        `${baseUrl}/tasks/${taskId}/duplicate`,
        {
          taskTitle: data?.taskTitle,
          taskDescription: data?.taskDescription,
          parentTaskId: data?.parentTaskId,
          projects: data?.projects,
          files: data?.files,
          assignee: data?.assignee,
          tags: data?.tags,
          dueDate: data?.dueDate,
          subtasks: data?.subtasks,
          collaborators: data?.collaborators,
        },
        config,
      );

      const normalizedResp = normalize(
        res?.data,
        SCHEMAS.ADD_TASK_EVENT_SCHEMA,
      );

      const task =
        normalizedResp?.entities?.tasks?.[normalizedResp?.result?.data] || {};
      const projectId = task?.projectId;
      const customFields = getCustomFieldsForProjectId(getState(), {
        projectId,
      });

      dispatch({
        type: ACTION_TYPES.ADD_TASK_SUCCESS,
        payload: {
          taskId: normalizedResp?.result?.data,
          task: {
            ...task,
            projectAndSectionLabelList: {
              [task?.projectId]: {
                projectId: task?.projectId,
                sectionId: task?.sectionId,
              },
            },
            extendedCustomFields: res?.data?.data?.customFields,
          },
          assignees: normalizedResp?.entities?.assignees,
          users: normalizedResp?.entities?.users,
          hasOpenedTask,
        },
      });

      notification.success({
        message:
          srcTask?.titleIdentifier && task?.titleIdentifier
            ? `You just duplicated ${srcTask?.titleIdentifier} → ${task?.titleIdentifier}`
            : 'You just duplicated a task',
        placement: 'bottomRight',
      });

      // callback && callback(res?.data?.data);
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.ADD_TASK_ERROR,
        payload: error,
      });
      notification.error({
        message: error?.response?.data?.message || 'Oops! somthing Went Wrong',
        placement: 'bottomRight',
      });
    }
    // const task = omit(getTaskById(getState(), { taskId }), ['_id', 'assignee']);

    // if (isEmpty(task)) {
    //   return;
    // }

    // const taskData = {
    //   ...task,
    //   taskTitle: task?.taskTitle,
    //   taskDescription: task?.taskDescription,
    //   cost: 0,
    //   dueDate: task?.dueDate,
    //   section: task?.sectionId,
    //   sectionCounts: {},
    //   isCompleted: false,
    //   documentPath: '',
    //   projects: [
    //     {
    //       section: task?.sectionId,
    //       projectId: task?.projectId,
    //       position: 'bottom',
    //     },
    //   ],
    //   addedTo: 'bottom',
    //   parentTaskId: null,
    // };

    // dispatch(addTaskAction(taskData));
  };

export const markTaskAsCompleteIncomplete =
  (taskId, intl) => async (dispatch, getState) => {
    const task = getTaskById(getState(), { taskId });

    if (isEmpty(task)) {
      return;
    }

    await dispatch(
      editTaskAction(
        {
          _id: taskId,
          isCompleted: !task?.isCompleted,
        },
        () => {
          notification.success({
            message: intl.formatMessage({
              id: task?.isCompleted ? 'INCOMPLETE' : 'COMPLETED',
              defaultMessage: 'Completed',
            }),
            placement: 'bottomRight',
          });
        },
      ),
    );
  };

export const copyTaskLink =
  (taskId, titleIdentifier, intl) => (dispatch, getState) => {
    const task = getTaskById(getState(), { taskId });

    if (isEmpty(task) && !titleIdentifier && !task?.titleIdentifier) {
      return;
    }

    const { pathname, origin } = window.location;

    navigator.clipboard.writeText(
      origin +
        pathname +
        '?selectedTask=' +
        (task?.titleIdentifier || titleIdentifier),
    );
    notification.success({
      message: 'Task link copied to clipboard',
      placement: 'bottomRight',
    });
  };

// FIXME: no reducer for the action. Better to move to utils or add a proper action with reducer
export const bulkAddTasksAction = (data, callback) => async (dispatch) => {
  try {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const res = await axios.post(
      `${baseUrl}/tasks/bulk/add`,
      { tasks: data },
      config,
    );
    notification.success({
      message: 'Adding Tasks!',
      placement: 'bottomRight',
    });

    dispatch({
      type: ACTION_TYPES.BULK_ADD_TASKS_SUCCESS,
    });
    callback && callback(res?.data?.data?.createdTasks);
    return res?.data?.data;
  } catch (error) {
    notification.error({
      message: error?.response?.data?.message || 'Oops! somthing Went Wrong',
      placement: 'bottomRight',
    });
  }
};

// FIXME: no reducer for the action. Better to move to utils or add a proper action with reducer
export const getAiTasksAction = (text, callback) => async (dispatch) => {
  try {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const res = await axios.post(
      `${baseUrl}/ai/tasks`,
      {
        query: text,
      },
      config,
    );

    dispatch({
      type: ACTION_TYPES.GET_AI_TASKS_SUCCESS,
    });

    return res?.data?.tasks;
  } catch (error) {
    console.log('---- error', error);
  }
};

export const clearOpenedTaskId = () => (dispatch) => {
  dispatch({
    type: ACTION_TYPES.CLEAR_OPENED_TASK_ID,
  });
};

const ALL_TASKS_CACHE_KEY = (wsId) => `workflo_all_tasks_${wsId}`;
const ALL_TASKS_CACHE_LIMIT = 30;

const PROJECT_TASKS_CACHE_KEY = (projectId) => `workflo_project_tasks_${projectId}`;
const RECENT_PROJECTS_CACHE_KEY = 'workflo_recent_projects';
const RECENT_PROJECTS_MAX = 3;

const saveRecentProject = (projectId) => {
  try {
    const existing = JSON.parse(localStorage.getItem(RECENT_PROJECTS_CACHE_KEY) || '[]');
    const updated = [projectId, ...existing.filter((id) => id !== projectId)].slice(0, RECENT_PROJECTS_MAX);
    localStorage.setItem(RECENT_PROJECTS_CACHE_KEY, JSON.stringify(updated));
  } catch (_) {}
};

export const loadCachedProjectTasks = (projectId) => (dispatch) => {
  try {
    const raw = localStorage.getItem(PROJECT_TASKS_CACHE_KEY(projectId));
    if (!raw) return;
    const { tasks, sections, tasksResults } = JSON.parse(raw);
    if (!tasksResults?.length) return;
    dispatch({
      type: ACTION_TYPES.GET_TASKS_REQUEST_SUCCESS,
      payload: { tasks, sections, tasksResults, assignees: {}, users: {}, projectId, fromCache: true },
    });
  } catch (_) {}
};

export const loadCachedUserTasks = (wsId) => (dispatch) => {
  try {
    const workspaceId = wsId || sessionStorage.getItem('workspaceId') || localStorage.getItem('workspaceId');
    const raw = localStorage.getItem(ALL_TASKS_CACHE_KEY(workspaceId));
    if (!raw) return;
    const { tasks, tasksIdList } = JSON.parse(raw);
    if (!tasksIdList?.length) return;
    dispatch({
      type: ACTION_TYPES.GET_USER_TASKS_REQUEST_SUCCESS,
      payload: { tasks, tasksIdList, assignees: {}, users: {}, count: tasksIdList.length, page: 1, fromCache: true },
    });
  } catch (_) {}
};

export const getUserTasksAction =
  (filters = {}, page, wsId, knownTotal) =>
  async (dispatch) => {
    try {
      const workspaceId =
        wsId ||
        sessionStorage.getItem('workspaceId') ||
        localStorage.getItem('workspaceId');

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;

      const queryString = convertFiltersToQueryString(filters);

      // Send the viewer's timezone so date presets (today/tomorrow/overdue/
      // this-week) are bucketed by the user's calendar day, not UTC.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // On pagination (page > 1) send the total we already have so the server
      // skips recomputing the (page-invariant) count — the dominant per-page
      // server cost. Omitted on page 1, where the count must be computed.
      const knownTotalParam =
        page > 1 && knownTotal != null ? { totalCount: knownTotal } : {};

      // v2 all-tasks filter (denormalized projectIds access path). ON by
      // default; see js/featureFlags.js for the localStorage /
      // REACT_APP_DISABLE_ALL_TASKS_V2_FILTER resolution order.
      const filterBaseUrl = isAllTasksV2FilterEnabled()
        ? baseUrl?.replace('/api/v1', '/api/v2')
        : baseUrl;

      const res = await axios.get(
        `${filterBaseUrl}/tasks/${workspaceId}/filter${queryString}`,
        {
          ...config,
          params: { page, timezone, ...knownTotalParam },
        },
      );

      const normalizedTasksResp = normalize(
        res?.data?.data,
        SCHEMAS.GET_ALL_TASKS_SCHEMA,
      );

      const payload = {
        assignees: normalizedTasksResp?.entities?.assignees,
        users: normalizedTasksResp?.entities?.users,
        tasks: normalizedTasksResp?.entities?.tasks,
        tasksIdList: normalizedTasksResp?.result?.tasks,
        count: res?.data?.data?.count,
        page,
      };

      dispatch({ type: ACTION_TYPES.GET_USER_TASKS_REQUEST_SUCCESS, payload });

      // Cache first 30 task ids + entities for instant rehydration on next visit
      if (!page || page === 1) {
        try {
          const idList = (payload.tasksIdList || []).slice(0, ALL_TASKS_CACHE_LIMIT);
          const taskEntities = Object.fromEntries(idList.map((id) => [id, payload.tasks?.[id]]).filter(([, v]) => v));
          localStorage.setItem(ALL_TASKS_CACHE_KEY(workspaceId), JSON.stringify({ tasks: taskEntities, tasksIdList: idList }));
        } catch (_) {}
      }
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.GET_USER_TASKS_REQUEST_ERROR,
        payload: error,
      });
    }
  };

// FIXME: no reducer for the action. Better to move to utils or add a proper action with reducer
export const downloadTaskUrlAction = (data, callback) => async () => {
  try {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const res = await axios.post(`${baseUrl}/tasks/downloadUrl`, data, config);
    callback && callback();
    return res.data?.data || {};
  } catch (error) {
    console.log(error);
  }
};

// FIXME: no reducer for the action. Better to move to utils or add a proper action with reducer
export const uploadTaskUrlAction =
  (data = {}, callback, intl) =>
  async () => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      data.workspaceId = sessionStorage.getItem('workspaceId');
      const res = await axios.post(`${baseUrl}/tasks/uploadUrl`, data, config);
      callback && callback();
      return res.data?.data || {};
    } catch (error) {
      console.log(error);
      notification.error({
        message:
          error?.response?.data?.message ||
          intl?.formatMessage({
            id: 'OOPS_ERROR',
            defaultMessage: 'Oops! something Went Wrong',
          }),
        placement: 'bottomRight',
      });
    }
  };

// FIXME: no reducer for the action. Better to move to utils or add a proper action with reducer
export const deleteTaskFileAction = (taskId, fileId, callback) => async () => {
  try {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const res = await axios.delete(
      `${baseUrl}/tasks/${taskId}/files/${fileId}`,
      config,
    );
    callback && callback();
    return res.data?.data || {};
  } catch (error) {
    console.log(error);
  }
};

export const addTaskToProjectAction =
  (taskId, projectId) => async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.put(
        `${baseUrl}/tasks/project/add`,
        { projectId, taskId, position: 'bottom' },
        config,
      );
      await dispatch(getProjectByIdBasicFieldsAction(projectId));
      dispatch({
        type: ACTION_TYPES.ADD_TASK_TO_PROJECT_REQUEST_SUCCESS,
        payload: {
          taskId,
          projectId,
          sectionId: res.data?.data?.taskProject?.section,
          position: res.data?.data?.taskProject?.position,
        },
      });

      if (localStorage.getItem('virtualizedListFeatureEnabled') === 'true') {
        dispatch(
          addTaskToVirtualizedListAction(
            {
              _id: taskId,
              projectId,
              sectionId: res.data?.data?.taskProject?.section,
              addedTo: 'bottom',
            },
            true,
          ),
        );
      }
    } catch (error) {
      console.log(error);
      dispatch({
        type: ACTION_TYPES.ADD_TASK_TO_PROJECT_REQUEST_FAILURE,
      });
    }
  };

export const removeTaskFromProjectAction =
  (taskId, projectId, callback) => async (dispatch, getState) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.put(
        `${baseUrl}/tasks/project/remove`,
        { projectId: projectId, taskId },
        config,
      );
      callback && callback();

      const state = getState();
      const projectAndSectionLabelList = getTaskProjectAndSectionLabelList(
        state,
        { taskId },
      );
      const sectionId = projectAndSectionLabelList?.[projectId]?.sectionId;
      const customFieldsIdList = getCustomFieldsIdListForProjectId(state, {
        projectId,
      });

      dispatch({
        type: ACTION_TYPES.REMOVE_PROJECT_FROM_TASK_SUCCESS,
        payload: {
          taskId,
          projectId,
          sectionId,
          customFieldsIdList,
        },
      });
      return res.data?.data || {};
    } catch (error) {
      console.log(error);
    }
  };

export const addCommentToTaskAction = (comment) => ({
  type: ACTION_TYPES.ADD_COMMENT_TO_TASK,
  payload: comment,
});

export const updateTaskEventAction = (task) => async (dispatch, getState) => {
  const state = getState();

  const normalizedTask = normalize({ data: task }, SCHEMAS.GET_TASK_SCHEMA);

  const taskId = normalizedTask?.result?.data;
  const currentTask = getTaskById(state, { taskId });
  const currentProjectAndSectionLabelList = Object.values(
    currentTask?.projectAndSectionLabelList || {},
  );

  const prevProjects = [];
  const prevSections = [];
  if (!isEmpty(currentProjectAndSectionLabelList)) {
    currentProjectAndSectionLabelList?.forEach(({ projectId, sectionId }) => {
      prevProjects.push(projectId);
      prevSections.push(sectionId);
    });
  } else {
    currentTask?.projectId && prevProjects.push(currentTask?.projectId);
    currentTask?.sectionId && prevSections.push(currentTask?.sectionId);
  }

  /** in case if the task needs to be updated only if the task is opened or project is opened uncomment below code: */
  // const { 1: currentProjectId } = window.location.pathname.split('/');
  // const selectedTaskParam = new URLSearchParams(window.location.search).get('selectedTask');
  // const taskIdentifier = task?.titleIdentifier;
  // if (taskIdentifier?.toString() !== selectedTaskParam?.toString() && !prevProjects.includes(currentProjectId)) {
  //   return;
  // }

  const taskObject = normalizedTask?.entities?.tasks?.[taskId];
  const updatedTask = {
    ...taskObject,
    projectAndSectionLabelList: Object.keys(
      taskObject?.projectAndSectionLabelList,
    )?.reduce((result, currentKey) => {
      const currentValue =
        taskObject?.projectAndSectionLabelList?.[currentKey] || {};
      const currentReorderTime = getTaskLastReorderTimeForProjectId(state, {
        taskId,
        projectId: currentValue.projectId,
      });

      return {
        ...result,
        [currentKey]: {
          ...currentValue,
          isOutdated:
            currentReorderTime && currentReorderTime > task?.requestStartTime,
        },
      };
    }, {}),
  };

  dispatch({
    type: ACTION_TYPES.UPDATE_TASK_EVENT,
    payload: {
      assignees: normalizedTask?.entities?.assignees,
      task: updatedTask,
      users: normalizedTask?.entities?.users,
      taskId: normalizedTask?.result?.data,
      prevProjects,
      prevSections,
    },
  });
};

export const addTaskEventAction = (taskData) => async (dispatch, getState) => {
  const normalizedTask = normalize(
    { data: taskData },
    SCHEMAS.ADD_TASK_EVENT_SCHEMA,
  );

  const taskId = normalizedTask?.result?.data;
  const task = normalizedTask?.entities?.tasks?.[taskId];

  dispatch(addTaskToVirtualizedListAction(task));

  dispatch({
    type: ACTION_TYPES.ADD_TASK_EVENT,
    payload: {
      taskId,
      task: {
        ...task,
        projectAndSectionLabelList: {
          [task?.projectId]: {
            projectId: task?.projectId,
            sectionId: task?.sectionId,
          },
        },
      },
      assignees: normalizedTask?.entities?.assignees,
      users: normalizedTask?.entities?.users,
      subtasks: normalizedTask?.entities?.subtasks
    },
  });
};

export const addSubTaskEventAction = (taskData) => async (dispatch) => {
  const normalizedTask = normalize(
    { data: taskData },
    SCHEMAS.ADD_TASK_EVENT_SCHEMA,
  );

  const taskId = normalizedTask?.result?.data;
  const task = normalizedTask?.entities?.tasks?.[taskId];

  dispatch({
    type: ACTION_TYPES.ADD_SUBTASK_EVENT,
    payload: {
      taskId,
      task: {
        ...task,
        projectAndSectionLabelList: {
          [task?.projectId]: {
            projectId: task?.projectId,
            sectionId: task?.sectionId,
          },
        },
      },
      assignees: normalizedTask?.entities?.assignees,
      users: normalizedTask?.entities?.users,
    },
  });
};

export const removeTaskEventAction = (taskId) => async (dispatch, getState) => {
  const state = getState();
  const isCompleted = isTaskCompletedSelector(state, { taskId });
  const sectionId = getTaskSectionId(state, { taskId });

  const openedTaskId = getOpenedTaskId(state);

  if (taskId === openedTaskId) {
    dispatch(isTicketOpen(false));
  }

  dispatch({
    type: ACTION_TYPES.REMOVE_TASK_EVENT,
    payload: {
      taskId,
      sectionId,
      isCompleted,
    },
  });
  dispatch({
    type: ACTION_TYPES.REMOVE_TASK_FROM_INFINITE_LIST,
    payload: {
      taskId,
      sectionId,
    },
  });
};

export const setProjectTasksFiltersAction =
  (projectId, filters, resetFilters = false) =>
  (dispatch, getState) => {
    dispatch({
      type: ACTION_TYPES.SET_PROJECT_TASKS_FILTERS,
      payload: {
        filters: filters,
        reset: resetFilters,
      },
    });
    const state = getState();
    const sorts = getProjectTasksSorts(state);
    dispatch(getTasksAction(projectId || filters?.projectId, filters, sorts));
  };

export const setUserTasksFiltersAction =
  (filters, resetFilters = false) =>
  (dispatch) => {
    try {
      dispatch({
        type: ACTION_TYPES.SET_USER_TASKS_FILTERS,
        payload: {
          filters: filters,
          reset: resetFilters,
        },
      });
    } catch (e) {
      console.log('----- e', e);
    }
  };

export const setProjectTasksSortsAction =
  (projectId, sorts) => async (dispatch, getState) => {
    dispatch({
      type: ACTION_TYPES.SET_PROJECT_TASKS_SORTS,
      payload: {
        sorts,
      },
    });
    const state = getState();
    const filters = getProjectTasksFilters(state);
    dispatch(getTasksAction(projectId, filters, sorts));
  };

export const deleteCommentFromTaskAction =
  (messageId, taskId) => (dispatch) => {
    dispatch({
      type: ACTION_TYPES.DELETE_COMMENT_FROM_TASK,
      payload: {
        messageId,
        taskId,
      },
    });
  };

export const getDashboardTasksAction =
  (workspaceId, callback) => async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      workspaceId = workspaceId || sessionStorage.getItem('workspaceId');
      if (!workspaceId) {
        return;
      }
      const res = await axios.get(
        `${baseUrl}/workspaces/${workspaceId}/tasks`,
        config,
      );
      callback && callback();

      const normalizedTasksResp = normalize(
        res?.data?.data,
        SCHEMAS.GET_WORKSPACE_TASKS_SCHEMA,
      );
      dispatch({
        type: ACTION_TYPES.GET_DASHBOARD_TASKS_REQUEST_SUCCESS,
        payload: {
          assignees: normalizedTasksResp?.entities?.assignees,
          users: normalizedTasksResp?.entities?.users,
          tasks: normalizedTasksResp?.entities?.tasks,
          tasksIdList: normalizedTasksResp?.result?.tasks,
          completedTasks: res?.data?.data?.completedTasks,
          totalTasks: res?.data?.data?.totalTasks,
          unassignedTasks: res?.data?.data?.unassignedTasks,
        },
      });
    } catch (error) {
      dispatch({
        type: ACTION_TYPES.GET_DASHBOARD_TASKS_REQUEST_ERROR,
        payload: error?.response?.data?.message,
      });
      notification.error({
        message: error?.response?.data?.message || 'Oops! something Went Wrong',
        placement: 'bottomRight',
      });
    }
  };

export const reorderTasksAction =
  (fromSection, toSection, taskId, fromIndex, toIndex, projectId, rearrangedData) =>
  (dispatch, getState) => {
    const state = getState();
    const loggedUser = state?.userReducer?.user;

    const isSameSection = fromSection === toSection;
    const sortedToSectionTasksIdList = getSortedTaskIdListBySectionId(state, {
      sectionId: toSection,
      projectId,
    });
    const isCompleted = isTaskCompletedSelector(state, { taskId });
    const prevPosition = getTaskPosition(state, { taskId });

    if (isSameSection && fromIndex === toIndex) {
      return;
    }

    const itemsIdListWithoutCurrent = without(
      sortedToSectionTasksIdList,
      taskId,
    );
    const affectedItemsIdList = itemsIdListWithoutCurrent.slice(
      Math.max(toIndex - 1, 0),
    );
    const firstAffectedItemId = affectedItemsIdList[0];

    const firstAffectedItem =
      state?.tasks?.entities?.[firstAffectedItemId] || {};
    const referenceItemPosition =
      toIndex < itemsIdListWithoutCurrent.length
        ? firstAffectedItem.position
        : state?.tasks?.entities?.[
            itemsIdListWithoutCurrent?.[itemsIdListWithoutCurrent.length - 1]
          ]?.position;

    // Get after items with same as beforeItem position if any
    // and fix the positions for affected items having the same position.
    // Ideally there should not be items with same position in one section
    let itemsWithSamePositionCount = 0;
    // first element with i = 0 is not needed here as it should not change the position (it is before item)
    for (let i = 1; i < affectedItemsIdList.length; i++) {
      const item = state?.tasks?.entities?.[affectedItemsIdList[i]] || {};
      if (item?.position !== referenceItemPosition) {
        break;
      }

      itemsWithSamePositionCount = itemsWithSamePositionCount + 1;

      const taskId = affectedItemsIdList[i];

      const prevPosition = getTaskPosition(state, { taskId });

      const indexInAffectedList = affectedItemsIdList.findIndex(
        (affectedTaskId) => affectedTaskId === taskId,
      );
      const itemsToIncreasePositionByOne =
        indexInAffectedList >= 0
          ? affectedItemsIdList.slice(indexInAffectedList + 1)
          : [];
      const newPosition = referenceItemPosition + itemsWithSamePositionCount;
      const reorderData = {
        lastIndex: referenceItemPosition, // @Umair, should it be the position of item before the destination or the previous position of the target item we are moving?
        preSection: toSection,
        newIndex: newPosition,
        section: toSection,
        taskId,
        userId: loggedUser?._id,
        _id: taskId,
        projectId,
      };
      // console.warn(`Moving item ${taskId} to position ${newPosition}`, reorderData, indexInAffectedList, itemsToIncreasePositionByOne);
      dispatch({
        type: ACTION_TYPES.TASKS_REORDER_ACTION,
        payload: {
          taskId,
          projectId,
          toSection,
          itemsToIncreasePositionByOne,
          newPosition,
        },
      });
      dispatch(socketSendAction('reorder-section', reorderData));
    }

    // Finally reorder the target item

    let newPosition = 0;

    if (typeof referenceItemPosition === 'number') {
      newPosition =
        toIndex === 0 ? referenceItemPosition - 1 : referenceItemPosition + 1;
    }

    const reorderData = {
      lastIndex: referenceItemPosition, // @Umair, should it be the position of item before the destination or the previous position of the target item we are moving?
      preSection: fromSection,
      newIndex: newPosition,
      section: toSection,
      taskId,
      userId: loggedUser?._id,
      _id: taskId,
      projectId,
    };
    // console.warn(`Moving item ${taskId} to position ${newPosition}`, reorderData);
    const itemsToIncreasePositionByOne = affectedItemsIdList.slice(1);
    dispatch({
      type: ACTION_TYPES.TASKS_REORDER_ACTION,
      payload: {
        taskId,
        projectId,
        toSection,
        itemsToIncreasePositionByOne,
        newPosition,
      },
    });
    dispatch(socketSendAction('reorder-section', reorderData));

    dispatch({
      type: ACTION_TYPES.TASKS_DRAG_N_DROP_ACTION,
      payload: {
        taskId,
        fromSection,
        toSection,
        projectId,
        isSameSection,
        isCompleted,
      },
    });

    if (localStorage.getItem('virtualizedListFeatureEnabled') === 'true') {
      if (!rearrangedData) {
        dispatch({
          type: ACTION_TYPES.INFINITE_LIST_ITEM_SECTION_CHANGE_ACTION,
          payload: {
            taskId,
            fromSection,
            toSection,
            projectId,
            isSameSection,
            isCompleted,
          },
        });
        return;
      }

      const updatedSections = rearrangedData.reduce(
        (resultData, infiniteListItem) => {
          if (
            infiniteListItem?._id?.includes(ADD_NEW_ROW_ID_PREFFIX) ||
            infiniteListItem?.isGroup
          ) {
            return resultData;
          }

          return {
            ...resultData,
            [infiniteListItem?.sectionId]: {
              virtualizedIdList: [
                ...(resultData?.[infiniteListItem?.sectionId]
                  ?.virtualizedIdList || []),
                infiniteListItem?._id,
              ],
            },
          };
        },
        {},
      );

      dispatch({
        type: ACTION_TYPES.INFINITE_LIST_ITEM_DRAG_N_DROP_ACTION,
        payload: {
          projectId,
          sections: updatedSections,
        },
      });
    }
  };

export const reorderVirtualizedListTasksAction =
  (fromSection, toSection, taskId, taskIdOnNewPosition, projectId, rearrangedData) =>
  (dispatch, getState) => {
    const fn = '[reorderVirtualizedListTasksAction]';
    const state = getState();
    const sortedToSectionTasksIdList = getSortedTaskIdListBySectionId(state, {
      sectionId: toSection,
      projectId,
    });

    let toIndex;

    if (taskIdOnNewPosition) {
      const indexInNewSection = without(
        sortedToSectionTasksIdList,
        taskId,
      )?.findIndex((taskId) => taskId === taskIdOnNewPosition);
      toIndex = indexInNewSection < 0 ? -1 : indexInNewSection + 1;
    } else {
      toIndex = 0;
    }

    if (toIndex < 0) {
      console.warn(
        `${fn}: failed to get toIndex. Going to cancel reorder tasks action`,
      );
      return;
    }

    dispatch(
      reorderTasksAction(
        fromSection,
        toSection,
        taskId,
        undefined,
        toIndex,
        projectId,
        rearrangedData,
      ),
    );
  };

export const reorderTasksEventAction =
  (data = {}) =>
  (dispatch, getState) => {
    if (isEmpty(data)) {
      return;
    }
    const { from, projectId, taskId, tasks, to, requestStartTime } = data;
    const state = getState();
    const isSameSection = from === to;
    const isCompleted = isTaskCompletedSelector(state, { taskId });
    dispatch({
      type: ACTION_TYPES.TASKS_REORDER_EVENT,
      payload: {
        taskId,
        fromSection: from,
        toSection: to,
        projectId,
        isSameSection,
        isCompleted,
        tasks,
        requestStartTime,
      },
    });
  };

export const addTaskCustomFieldAction =
  (data = {}, projectId) =>
  (dispatch, getState) => {
    const { description, name, possibleValues, type, _id } = data;
    const taskId = getOpenedTaskId(getState());

    if (!taskId) return;

    dispatch({
      type: ACTION_TYPES.ADD_TASK_CUSTOM_FIELD_ACTION,
      payload: {
        taskId,
        description,
        name,
        possibleValues,
        type,
        _id,
        projectId,
      },
    });
  };

export const updateTaskCustomFieldAction =
  (data = {}) =>
  (dispatch, getState) => {
    const { description, name, possibleValues, type, _id } = data;
    const taskId = getOpenedTaskId(getState());

    if (!taskId) return;

    dispatch({
      type: ACTION_TYPES.UPDATE_TASK_CUSTOM_FIELD_ACTION,
      payload: {
        taskId,
        description,
        name,
        possibleValues,
        type,
        _id,
      },
    });
  };

export const deleteTaskCustomFieldAction =
  (customFieldId) => (dispatch, getState) => {
    const taskId = getOpenedTaskId(getState());

    if (!taskId) return;

    dispatch({
      type: ACTION_TYPES.DELETE_TASK_CUSTOM_FIELD_ACTION,
      payload: {
        taskId,
        customFieldId,
      },
    });
  };

export const setRecurringTaskAction =
  ({
    calendarDate,
    duration,
    frequency,
    isTriggerComplete,
    iteration,
    taskId,
  }) =>
  (dispatch, getState) => {
    if (!taskId || !calendarDate) return;

    // TODO: task.projects field is pretty unreliable. It might be empty or contain only current project
    // better to use new tasksToProjectsRelation store
    const sections = getTaskProjectWithSectionIdList(getState(), { taskId });

    dispatch(
      editTaskAction(
        {
          _id: taskId,
          dueDate: calendarDate,
          recurringConfig: {
            recurring: true,
            recurringDuration: {
              duration: duration, // any numeric value
              unit: frequency, //allowed values are day, week, year
              iteration: iteration,
            },
            trigger: isTriggerComplete ? 'done' : 'auto',
            projectSections: sections, // This array stores the destination section for recurring tasks. It is an array because the task may be in multiple projects.
          },
        },
        null,
        () => {
          notification.success({
            message: 'Task set to recurring!',
            placement: 'bottomRight',
          });
        },
      ),
    );
  };

export const uploadFormUrl = (data = {}, callback) => {
  return async () => {
    try {
      const res = await axios.post(`${baseUrl}/form/uploadUrl`, data, config);
      callback && callback();
      return res.data?.data || {};
    } catch (error) {
      console.log(error);
      notification.error({
        message: error?.response?.data?.message || 'Oops! something Went Wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const toggleTaskWatch = (taskId, isMuted, users = [], callback) => {
  return async () => {
    try {
      const res = await axios.post(
        `${baseUrl}/tasks/${taskId}/${isMuted ? 'watch' : 'unwatch'}`,
        {
          userIds: users,
        },
        config,
      );

      callback && callback();
      return res.data?.data || {};
    } catch (error) {
      console.log(error);
      notification.error({
        message: error?.response?.data?.message || 'Oops! something Went Wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const addTaskAttachment =
  (file, taskId) => async (dispatch, getState) => {
    try {
      const { signedUrl: uploadSignedUrl, fileId } = await dispatch(
        uploadTaskUrlAction({
          fileName: file.name,
          taskId: taskId,
          type: file?.type,
          fileSize: file?.size,
        }),
      );

      if (uploadSignedUrl) {
        await dispatch(signedUploadUrl(uploadSignedUrl, file));
      }

      const taskFiles = getTaskFiles(getState(), { taskId });
      const allFiles = [...taskFiles, fileId];

      await dispatch(
        editTaskAction({
          _id: taskId,
          files: allFiles,
          attachments: [],
        }),
      );
      try {
        const { signedUrl } = await dispatch(getFileActionWithRetry(fileId));
        return { signedUrl, fileId };
      } catch (e) {
        return null;
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  };

export const openTaskAction =
  (doNavigation, taskTitleIdentifier, taskId) => async (dispatch, getState) => {
    const state = getState();
    const openedTaskId = getOpenedTaskId(state);
    const openedTaskTitleIdentifier = getOpenedTaskTitleIdentifier(state);

    if (
      openedTaskId === taskId ||
      openedTaskTitleIdentifier === taskTitleIdentifier
    ) {
      return;
    }

    dispatch(isChatOpen(false));
    dispatch(setTeamsState(false));

    if (doNavigation) {
      const params = new URLSearchParams(window.location.search);
      params.delete('msgId'); // Remove the specified parameter
      params.set('selectedTask', taskTitleIdentifier);
      // Construct the new URL with updated parameters
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      // Use navigate to replace the current URL
      dispatch(navigateAction(newUrl));
    }

    if (taskId) {
      dispatch({
        type: ACTION_TYPES.OPEN_TASK_ACTION,
        payload: {
          taskId,
        },
      });
    }

    dispatch(isTicketOpen(true));

    const requestedTaskId = await dispatch(
      getTaskByIdentifierAction(
        taskTitleIdentifier,
        taskId,
        'openTaskAction',
        false,
        (errorStatus) => { if (errorStatus) dispatch(isTicketOpen(false)); },
      ),
    );

    if (!taskId && requestedTaskId) {
      dispatch({
        type: ACTION_TYPES.OPEN_TASK_ACTION,
        payload: {
          taskId: requestedTaskId,
        },
      });
    }
  };

export const deleteCustomFieldFromProject = (customFieldId) => (dispatch) => {
  dispatch({
    type: ACTION_TYPES.DELETE_CUSTOM_FIELD_FROM_PROJECT,
    payload: {
      customFieldId,
    },
  });
};

// Properly shapes data and loads chart-slice preview tasks into the task & assignee entity stores.
export const mergeChartPreviewTasksAction = (tasksArray) => (dispatch, getState) => {
  const processedTasks = tasksArray.map((task) => ({
    ...task,
    assignee: (task.assignee || []).map((a) =>
      typeof a?.assigneeId === 'string'
        ? { assigneeId: { _id: a.assigneeId } }
        : a,
    ),
  }));

  const normalized = normalize({ tasks: processedTasks }, SCHEMAS.GET_ALL_TASKS_SCHEMA);

  const members = getState().workspaceReducer?.currentWorkspace?.members || [];
  const memberMap = {};
  members.forEach((m) => { if (m?._id) memberMap[m._id] = m; });

  const normalizedAssignees = normalized.entities?.assignees || {};
  const assignees = {};
  Object.entries(normalizedAssignees).forEach(([id, entity]) => {
    const m = memberMap[id] || {};
    assignees[id] = {
      ...m,
      ...entity,
      avi: entity.avi || m.avi || null,
    };
  });

  dispatch({
    type: ACTION_TYPES.MERGE_CHART_PREVIEW_TASKS,
    payload: {
      tasks: normalized.entities?.tasks || {},
      assignees,
    },
  });
};
